/**
 * Contains functions for making requests to the tenants backend API endpoints
 * Using Axios HTTP client
 */

import axios from 'axios';
import auth from '../Auth/Auth';  // Import the auth utility

import BASE_URL from '../config';

// API base URL
const API_BASE_URL = BASE_URL;

// Helper function to get auth headers
const getAuthHeaders = () => {
  const token = auth.getToken();
  if (!token) return {};
  
  return {
    'Authorization': `Bearer ${token}`
  };
};

// Create an axios instance with default config
const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  }
});

// Add request interceptor to attach authentication token to all requests
api.interceptors.request.use(
  (config) => {
    // Add authorization header with JWT token if available
    const token = auth.getToken();
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

export const patchTenant = async (tenantId, tenantData) => {
  try {
    return await api.patch(`/tenants/${tenantId}/update/`, tenantData);
  } catch (error) {
    console.error(`Error patching tenant ${tenantId}:`, error);
    throw error;
  }
};

// Add response interceptor for error handling
api.interceptors.response.use(
  response => response.data,
  error => {
    const errorMessage = error.response?.data?.error || error.message;
    console.error('API Error:', errorMessage);
    return Promise.reject(errorMessage);
  }
);

// =============================================================================
// TENANT CRUD OPERATIONS
// =============================================================================

/**
 * Get all tenants
 * @returns {Promise} Promise resolving to array of tenant objects
 */
export const getAllTenants = async ({ includeInactive = false } = {}) => {
  try {
    const url = includeInactive ? '/tenants/?include_inactive=true' : '/tenants/';
    const response = await api.get(url);

    const data = response.results ?? (Array.isArray(response) ? response : response);
    console.log('✅ Tenants fetched:', data.length, 'records');
    console.log('📋 Sample additionalPhones:', data.slice(0, 3).map(t => ({
      name: t.name,
      additionalPhones: t.additionalPhones,
    })));

    return data;
  } catch (error) {
    console.error('❌ Error fetching tenants:', error);
    throw error;
  }
};



/**
 * Get a specific tenant by ID
 * @param {number} tenantId - ID of the tenant to retrieve
 * @returns {Promise} Promise resolving to tenant object
 */
export const getTenant = async (tenantId) => {
  try {
    const response = await api.get(`/tenants/${tenantId}/`);
    console.log(`Fetched tenant ${tenantId} data:`, response.data);
    return response;
  } catch (error) {
    console.error(`Error fetching tenant ${tenantId}:`, error);
    throw error;
  }
};


/**
 * Create a new tenant
 * @param {Object} tenantData - Data for the new tenant
 * @returns {Promise} Promise resolving to the created tenant object
 */
export const createTenant = async (tenantData) => {
  return await api.post('/tenants/create/', tenantData);
};

/**
 * Update an existing tenant
 * @param {number} tenantId - ID of the tenant to update
 * @param {Object} tenantData - Updated tenant data
 * @returns {Promise} Promise resolving to the updated tenant object
 */
export const updateTenant = async (tenantId, tenantData) => {
  try {
    return await api.put(`/tenants/${tenantId}/update/`, tenantData);
  } catch (error) {
    console.error(`Error updating tenant ${tenantId}:`, error);
    throw error;
  }
};

/**
 * Delete a tenant
 * @param {number} tenantId - ID of the tenant to delete
 * @returns {Promise} Promise resolving to success message
 */
export const deleteTenant = async (tenantId) => {
  try {
    return await api.delete(`/tenants/${tenantId}/delete/`);
  } catch (error) {
    console.error(`Error deleting tenant ${tenantId}:`, error);
    throw error;
  }
};

// =============================================================================
// LEASE MANAGEMENT
// =============================================================================

/**
 * Set lease end date for a tenant
 * @param {number} tenantId - ID of the tenant
 * @param {string} leaseEndDate - New lease end date in YYYY-MM-DD format
 * @returns {Promise} Promise resolving to the updated tenant object
 */
export const setLeaseEndDate = async (tenantId, leaseEndDate) => {
  try {
    return await api.post(`/tenants/${tenantId}/lease-end/`, { leaseEnd: leaseEndDate });
  } catch (error) {
    console.error(`Error setting lease end date for tenant ${tenantId}:`, error);
    throw error;
  }
};

// =============================================================================
// AVAILABLE UNITS
// =============================================================================

/**
 * Get all available units
 * @returns {Promise} Promise resolving to array of available unit objects
 */
export const getAvailableUnits = async () => {
  try {
    const response = await api.get('/tenants/available-units/');
    return response;
  } catch (error) {
    console.error('Error fetching available units:', error);
    throw error;
  }
};


// =============================================================================
// PAYMENT MANAGEMENT
// =============================================================================


/**
 * Get transactions for a specific tenant
 * @param {number} tenantId - ID of the tenant
 * @returns {Promise} Promise resolving to array of transaction objects
 */
export const getTenantTransactions = async (tenantId) => {
  try {
    console.log('🔍 Fetching transactions for tenant ID:', tenantId);
    
    const response = await api.get(`/tenants/${tenantId}/transactions/`);
    // Extract and return the results array instead of the full response
    const transactions = response.results || [];
    
    return transactions; // This was the fix needed
  } catch (error) {
    console.error(`❌ Error fetching transactions for tenant ${tenantId}:`, error);
    throw error;
  }
};


// =============================================================================
// WATER READINGS
// =============================================================================

/**
 * Record a water meter reading for a tenant
 * @param {Object} readingData - Water reading details
 * @returns {Promise} Promise resolving to the created water reading object
 */
export const recordWaterReading = async (readingData) => {
  try {
    return await api.post('/tenants/record-water-reading/', readingData);
  } catch (error) {
    console.error('Error recording water reading:', error);
    throw error;
  }
};

/**
 * Get water readings for a specific tenant
 * @param {number} tenantId - ID of the tenant
 * @returns {Promise} Promise resolving to array of water reading objects
 */
export const getTenantWaterReadings = async (tenantId) => {
  try {
    const response = await api.get(`/tenants/${tenantId}/water-readings/`);
    // Handle paginated response
    return response.results || response;
  } catch (error) {
    console.error(`Error fetching water readings for tenant ${tenantId}:`, error);
    throw error;
  }
};

/**
 * Get the latest water reading for a tenant
 * @param {number} tenantId - ID of the tenant
 * @returns {Promise} Promise resolving to latest reading data
 */
export const getTenantLatestReading = async (tenantId) => {
  try {
    console.log('🔍 Fetching latest water reading for tenant ID:', tenantId);
    
    const response = await api.get(`/tenants/${tenantId}/latest-reading/`);
    
    return {
      tenantId: response.tenantId,
      latestReading: response.latestReading,
      hasReadings: response.hasReadings,
      latestRate: response.latestRate,   // saved rate from last reading
    };
  } catch (error) {
    console.error(`❌ Error fetching latest reading for tenant ${tenantId}:`, error);
    throw error;
  }
};

/**
 * Reset all water readings for a tenant (use with caution)
 * @param {number} tenantId - ID of the tenant
 * @param {boolean} confirm - Confirmation flag (must be true)
 * @returns {Promise} Promise resolving to reset confirmation
 */
export const resetTenantWaterReadings = async (tenantId, confirm = false) => {
  try {
    if (!confirm) {
      throw new Error('Reset confirmation is required. Please set confirm parameter to true.');
    }
    
    console.log('⚠️ Resetting water readings for tenant ID:', tenantId);
    
    const response = await api.post(`/tenants/${tenantId}/reset-water-readings/`, {
      confirm: true
    });
    
    return {
      message: response.message,
      tenantId: response.tenantId,
      tenantName: response.tenantName
    };
  } catch (error) {
    console.error(`❌ Error resetting water readings for tenant ${tenantId}:`, error);
    throw error;
  }
};

// =============================================================================
// UTILITY BILLS MANAGEMENT
// =============================================================================

/**
 * Add a new utility bill for a tenant
 * @param {number} tenantId - ID of the tenant
 * @param {Object} billData - Utility bill data
 * @returns {Promise} Promise resolving to the created utility bill object
 */
export const addUtilityBill = async (tenantId, billData) => {
  try {
    return await api.post(`/tenants/${tenantId}/utility-bills/`, billData);
  } catch (error) {
    console.error(`Error adding utility bill for tenant ${tenantId}:`, error);
    throw error;
  }
};

/**
 * Update an existing utility bill
 * @param {number} tenantId - ID of the tenant
 * @param {number} billId - ID of the bill to update
 * @param {Object} billData - Updated utility bill data
 * @returns {Promise} Promise resolving to the updated utility bill object
 */
export const updateUtilityBill = async (tenantId, billId, billData) => {
  try {
    return await api.put(`/tenants/${tenantId}/utility-bills/${billId}/`, billData);
  } catch (error) {
    console.error(`Error updating utility bill ${billId} for tenant ${tenantId}:`, error);
    throw error;
  }
};

/**
 * Delete a utility bill
 * @param {number} tenantId - ID of the tenant
 * @param {number} billId - ID of the bill to delete
 * @returns {Promise} Promise resolving to success message
 */
export const deleteUtilityBill = async (tenantId, billId) => {
  try {
    return await api.delete(`/tenants/${tenantId}/utility-bills/${billId}/`);
  } catch (error) {
    console.error(`Error deleting utility bill ${billId}:`, error);
    throw error;
  }
};

/**
 * Generic function to manage utility bills (handles both add and update)
 * @param {number} tenantId - ID of the tenant
 * @param {Object} billData - Utility bill data
 * @param {number|null} billId - ID of the bill to update (null for new bills)
 * @returns {Promise} Promise resolving to the created/updated utility bill object
 */
export const manageUtilityBill = async (tenantId, billData, billId = null) => {
  if (billId) {
    return await updateUtilityBill(tenantId, billId, billData);
  } else {
    return await addUtilityBill(tenantId, billData);
  }
};


/**
 * Get utility bills for a specific tenant
 * @param {number} tenantId - ID of the tenant
 * @returns {Promise} Promise resolving to utility bills data
 */
export const getTenantUtilityBills = async (tenantId) => {
  try {
    const response = await api.get(`/tenants/${tenantId}/utility-bills/`);
    return response;
  } catch (error) {
    console.error(`Error fetching utility bills for tenant ${tenantId}:`, error);
    throw error;
  }
};

/**
 * Update tenant rent
 * @param {number} tenantId - ID of the tenant
 * @param {Object} rentData - Rent data object
 * @returns {Promise} Promise resolving to the updated rent response
 */
export const updateTenantRent = async (tenantId, rentData) => {
  try {
    return await api.put(`/tenants/${tenantId}/rent/`, rentData);
  } catch (error) {
    console.error(`Error updating rent for tenant ${tenantId}:`, error);
    throw error;
  }
};
// =============================================================================
// BALANCE & DEPOSIT ADJUSTMENTS
// =============================================================================

/**
 * Manually update a tenant's balance (creates an audit log entry on the server).
 * @param {number} tenantId
 * @param {{ balance: number, reason?: string }} data
 */
export const updateTenantBalance = async (tenantId, data) => {
  try {
    return await api.patch(`/tenants/${tenantId}/balance/`, data);
  } catch (error) {
    console.error(`Error updating balance for tenant ${tenantId}:`, error);
    throw error;
  }
};

/**
 * Manually update a tenant's security deposit (creates an audit log entry on the server).
 * @param {number} tenantId
 * @param {{ deposit: number, reason?: string }} data
 */
export const updateTenantDeposit = async (tenantId, data) => {
  try {
    return await api.patch(`/tenants/${tenantId}/deposit/`, data);
  } catch (error) {
    console.error(`Error updating deposit for tenant ${tenantId}:`, error);
    throw error;
  }
};

// =============================================================================
// MONTHLY PROCESSING
// =============================================================================

/**
 * Process monthly charges for all tenants
 * @returns {Promise} Promise resolving to processing results
 */
export const processMonthlyCharges = async () => {
  try {
    return await api.post('/tenants/process-monthly-charges/');
  } catch (error) {
    console.error('Error processing monthly charges:', error);
    throw error;
  }
};

/**
 * Manual monthly processing for specific tenants
 * @param {Object} processingData - Processing data (tenant IDs, etc.)
 * @returns {Promise} Promise resolving to processing results
 */
export const manualMonthlyProcessing = async (processingData) => {
  try {
    return await api.post('/tenants/manual-monthly-processing/', processingData);
  } catch (error) {
    console.error('Error in manual monthly processing:', error);
    throw error;
  }
};

/**
 * Get monthly payment status for a tenant
 * @param {number} tenantId - ID of the tenant
 * @returns {Promise} Promise resolving to monthly status data
 */
export const getTenantMonthlyStatus = async (tenantId) => {
  try {
    return await api.get(`/tenants/${tenantId}/monthly-status/`);
  } catch (error) {
    console.error(`Error fetching monthly status for tenant ${tenantId}:`, error);
    throw error;
  }
};

/**
 * Get payment status overview for all company tenants
 * @returns {Promise} Promise resolving to company payment overview
 */
export const getCompanyPaymentOverview = async () => {
  try {
    return await api.get('/tenants/payment-overview/');
  } catch (error) {
    console.error('Error fetching company payment overview:', error);
    throw error;
  }
};

// =============================================================================
// PROPERTIES API (from your original code - assuming these exist elsewhere)
// =============================================================================

// Helper function for API requests with authentication headers
const authFetch = async (url, options = {}) => {
  const headers = {
    ...options.headers,
    ...(options.body ? { 'Content-Type': 'application/json' } : {}),
    ...getAuthHeaders()
  };

  const response = await fetch(`${API_BASE_URL}${url}`, {
    ...options,
    headers
  });

  const data = await response.json();
  if (!response.ok) {
    throw new Error(data.error || 'API request failed');
  }
  return data;
};

/**
 * Fetch all apartments with authentication
 * @returns {Promise} Promise resolving to array of apartment objects
 */
export const fetchApartments = async () => {
  try {
    const data = await authFetch('/apartments');
    if (!data.success) {
      throw new Error(data.error || 'Failed to fetch apartments');
    }
    return data.apartments;
  } catch (error) {
    console.error('Error fetching apartments:', error);
    throw error;
  }
};

/**
 * Fetch categories for an apartment with authentication
 * @param {number} apartmentId - ID of the apartment
 * @returns {Promise} Promise resolving to array of category objects
 */
export const fetchCategories = async (apartmentId) => {
  try {
    const data = await authFetch(`/apartments/${apartmentId}/categories`);
    if (!data.success) {
      throw new Error(data.error || 'Failed to fetch categories');
    }
    return data.categories;
  } catch (error) {
    console.error('Error fetching categories:', error);
    throw error;
  }
};

/**
 * Fetch units for a category with authentication
 * @param {number} categoryId - ID of the category
 * @returns {Promise} Promise resolving to array of unit objects
 */
export const fetchUnits = async (categoryId) => {
  try {
    const data = await api.get(`/categories/${categoryId}/units`);
    // Filter for available units - modify this according to your data structure
    const availableUnits = data.units.filter(unit => !unit.occupied);
    return availableUnits;
  } catch (error) {
    console.error(`Error fetching units for category ${categoryId}:`, error);
    throw error;
  }
};

/**
 * Get move-out financial preview for a tenant
 * Shows current balance, deposit, offset amounts before confirming move-out
 */
export const getMoveOutPreview = async (tenantId) => {
  try {
    return await api.get(`/tenants/${tenantId}/move-out-preview/`);
  } catch (error) {
    console.error(`Error fetching move-out preview for tenant ${tenantId}:`, error);
    throw error;
  }
};
 
/**
 * Submit move-out for a tenant
 * @param {number} tenantId
 * @param {{ movedOutDate, depositAction, unitReleaseStatus, notes }} data
 */
export const submitMoveOut = async (tenantId, data) => {
  try {
    return await api.post(`/tenants/${tenantId}/move-out/`, data);
  } catch (error) {
    console.error(`Error submitting move-out for tenant ${tenantId}:`, error);
    throw error;
  }
};
 
/**
 * Undo move-out for a tenant (within 3-month grace period)
 */
export const undoMoveOut = async (tenantId) => {
  try {
    return await api.post(`/tenants/${tenantId}/undo-move-out/`);
  } catch (error) {
    console.error(`Error undoing move-out for tenant ${tenantId}:`, error);
    throw error;
  }
};
 
/**
 * Check if a tenant with a given ID number already exists in this company.
 * Used by the create-tenant form to suggest linking instead of creating.
 * @param {string} idNumber
 */
export const checkTenantByIdNumber = async (idNumber) => {
  try {
    return await api.get(`/tenants/check-id/?idNumber=${encodeURIComponent(idNumber)}`);
  } catch (error) {
    console.error('Error checking tenant ID number:', error);
    throw error;
  }
};
 
/**
 * Link an existing tenant to an additional unit (creates a new tenancy row
 * with the same personal details but fresh financials).
 * @param {number} tenantId — the primary/existing tenant row ID
 * @param {{ unitId, leaseStart, leaseEnd, deposit, paymentMethod, utilityBills }} data
 */
export const linkTenantToUnit = async (tenantId, data) => {
  try {
    return await api.post(`/tenants/${tenantId}/link-unit/`, data);
  } catch (error) {
    console.error(`Error linking tenant ${tenantId} to unit:`, error);
    throw error;
  }
};
 
/**
 * Get all tenants — supports optional include_inactive flag
 * REPLACE the existing getAllTenants function with this one
 */

// Expose the API object for other potential uses
export default api;